/**
 * Canonical user-facing error formatter for axios / fetch errors.
 *
 * Why this exists — live prod was leaking bare axios strings like
 * "Request failed with status code 500" into toasts even when the
 * underlying operation succeeded or when the server returned a perfectly
 * good structured `detail`. Liezel's file-create / conflict-check flow
 * showed "failed with status code" while the file WAS being created.
 *
 * This helper NEVER returns the bare "Request failed with status code N"
 * axios message. It walks a priority chain from most-specific server
 * payload to generic-but-meaningful copy, and only ever falls back to the
 * caller-supplied `fallback` — never to the noise.
 *
 * Priority chain:
 *   1. err.response.data.detail.message  (structured detail, e.g. session_guard)
 *   2. err.response.data.detail          IF a non-empty string
 *   3. err.response.data.message         IF a non-empty string
 *   4. err.response.data.error           IF a non-empty string
 *   5. 401/403  → session-expired copy
 *   6. 5xx      → generic server-error copy
 *   7. no response (network) → connectivity copy
 *   8. the caller's `fallback`
 *   9. err.message — but ONLY if it is not a bare axios status string
 *
 * For blob-typed error responses use `parseBlobError` (async) instead;
 * this helper handles the already-parsed JSON shapes.
 */

const AXIOS_STATUS_RE = /^Request failed with status code \d+/i;

// BM-H4024: FastAPI's stock body for a route that does not exist. It names
// no problem a member of staff can act on, so the caller's wording wins.
const FRAMEWORK_404 = "Not Found";

function firstString(...candidates) {
  for (const c of candidates) {
    if (typeof c === "string" && c.trim()) return c;
  }
  return null;
}

export function errorMessage(err, fallback = "Something went wrong") {
  const data = err?.response?.data;

  // 1–4: server-supplied payloads, most specific first.
  if (data && typeof data === "object") {
    const detail = data.detail;
    if (detail && typeof detail === "object") {
      const fromDetail = firstString(detail.message, detail.error);
      if (fromDetail) return fromDetail;
    }
    const fromBody = firstString(
      typeof detail === "string" && detail !== FRAMEWORK_404 ? detail : null,
      data.message,
      data.error,
    );
    if (fromBody) return fromBody;
  } else if (typeof data === "string" && data.trim()) {
    // Some endpoints return a plain-text body.
    return data;
  }

  const status = err?.response?.status;

  // 5: auth — the user can fix this themselves by reloading.
  if (status === 401 || status === 403) {
    return "Your session expired — please reload and try again.";
  }

  // 6: server-side failure — never surface the bare axios string here.
  if (typeof status === "number" && status >= 500) {
    return "Server error — please try again, and if it keeps happening tell the team.";
  }

  // 7: no response object at all → almost always a network/connectivity issue.
  if (err && !err.response) {
    return "Couldn't reach the server — check your connection and try again.";
  }

  // 8: caller's domain-specific fallback.
  if (typeof fallback === "string" && fallback.trim()) return fallback;

  // 9: last resort — the raw JS message, but NEVER the bare axios noise.
  const raw = err?.message;
  if (typeof raw === "string" && raw.trim() && !AXIOS_STATUS_RE.test(raw)) {
    return raw;
  }

  return "Something went wrong";
}

// Self-install as the global the codebase already guards for
// (`typeof window.errorMessage === "function"`). This upgrades the many
// existing guarded call sites to this canonical chain without touching
// each one. Direct `import { errorMessage }` is preferred for new code.
if (typeof window !== "undefined" && typeof window.errorMessage !== "function") {
  window.errorMessage = errorMessage;
}

export default errorMessage;
